import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './xentrios.css';

// Core pages
import HomePage from './pages/HomePage';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import AboutPage from './pages/AboutPage';
import DemoPage from './pages/DemoPage';
import InsightsPage from './pages/InsightsPage';

// Stack + category pages
import StackPage from './pages/StackPage';
import DentalRCMSoftwarePage from './pages/categories/DentalRCMSoftwarePage';
import DentalDataWarehousePage from './pages/categories/DentalDataWarehousePage';
import DentalAnalyticsPlatformPage from './pages/categories/DentalAnalyticsPlatformPage';
import DentalDenialManagementPage from './pages/categories/DentalDenialManagementPage';
import DentalPMSIntegrationPage from './pages/categories/DentalPMSIntegrationPage';
import CloudDentalPMSPage from './pages/categories/CloudDentalPMSPage';
import DSOPlatformPage from './pages/categories/DSOPlatformPage';

// Comparison pages
import RevTechVsDentalIntelligencePage from './pages/compare/RevTechVsDentalIntelligencePage';
import NDSVsKollaPage from './pages/compare/NDSVsKollaPage';
import XentriosVsLegacyPMSPage from './pages/compare/XentriosVsLegacyPMSPage';

// Best-of + market map pages
import BestDentalRCMPage from './pages/BestDentalRCMPage';
import BestDentalDataWarehousePage from './pages/BestDentalDataWarehousePage';
import BestDentalAnalyticsPage from './pages/BestDentalAnalyticsPage';
import DSOMarketMapPage from './pages/DSOMarketMapPage';


function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function TrackPageViews() {
  const { pathname } = useLocation();
  const trackerRef = React.useRef(null);
  useEffect(() => {
    const { VisitorTracker } = require('./tracker');
    trackerRef.current = new VisitorTracker(undefined, { site: 'xentrios' });
    return () => trackerRef.current?.destroy();
  }, []);
  useEffect(() => {
    if (trackerRef.current && !trackerRef.current.destroyed) {
      trackerRef.current._trackPageView();  // patches dwell on prev page then fires new page_view
    }
  }, [pathname]);
  return null;
}

function SolutionsDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          color: open ? '#fff' : 'var(--xen-gray)',
          fontSize: '0.85rem', fontWeight: 500, fontFamily: 'inherit',
          display: 'flex', alignItems: 'center', gap: '0.3rem', transition: 'color 0.2s'
        }}
        aria-expanded={open}
        aria-haspopup="true"
      >
        Solutions <span style={{ fontSize: '0.6rem', marginTop: '1px' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 0.75rem)', left: '50%',
          transform: 'translateX(-50%)', background: '#fff',
          borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          padding: '0.5rem', minWidth: 240, zIndex: 200,
          border: '1px solid #e8e9f0'
        }}>
          <DropdownSection label="Platform">
            <DropdownLink to="/stack" onClick={() => setOpen(false)}>Our Integrated Stack</DropdownLink>
            <DropdownLink to="/dso-platform" onClick={() => setOpen(false)}>DSO Operating Platform</DropdownLink>
          </DropdownSection>
          <DropdownSection label="By Category">
            <DropdownLink to="/dental-rcm-software" onClick={() => setOpen(false)}>Dental RCM Software</DropdownLink>
            <DropdownLink to="/dental-data-warehouse" onClick={() => setOpen(false)}>Dental Data Warehouse</DropdownLink>
            <DropdownLink to="/dental-denial-management" onClick={() => setOpen(false)}>Denial Management</DropdownLink>
            <DropdownLink to="/dental-analytics-platform" onClick={() => setOpen(false)}>Analytics Platform</DropdownLink>
            <DropdownLink to="/cloud-dental-pms" onClick={() => setOpen(false)}>Cloud Dental PMS</DropdownLink>
            <DropdownLink to="/dental-pms-integration" onClick={() => setOpen(false)}>PMS Integration</DropdownLink>
          </DropdownSection>
          <DropdownSection label="Compare">
            <DropdownLink to="/compare/revtech-vs-dental-intelligence" onClick={() => setOpen(false)}>RevTech vs Dental Intelligence</DropdownLink>
            <DropdownLink to="/compare/nds-vs-kolla" onClick={() => setOpen(false)}>NDS vs Kolla</DropdownLink>
            <DropdownLink to="/compare/xentrios-vs-legacy-pms" onClick={() => setOpen(false)}>Xentrios vs Legacy PMS</DropdownLink>
          </DropdownSection>
        </div>
      )}
    </div>
  );
}

function DropdownSection({ label, children }) {
  return (
    <div style={{ marginBottom: '0.25rem' }}>
      <div style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#999', padding: '0.4rem 0.6rem 0.2rem' }}>{label}</div>
      {children}
    </div>
  );
}

function DropdownLink({ to, onClick, children }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      style={{
        display: 'block', padding: '0.4rem 0.6rem', color: '#333',
        textDecoration: 'none', fontSize: '0.82rem', borderRadius: 6,
        transition: 'background 0.15s'
      }}
      onMouseEnter={e => e.currentTarget.style.background = '#f4f5fa'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      {children}
    </Link>
  );
}

function Layout({ children }) {
  return (
    <>
      <header className="xen-header">
        <div className="xen-container xen-header-inner">
          <Link to="/" className="xen-logo" onClick={() => window.scrollTo(0, 0)}>
            <img src="/images/xentrios-logo.png" alt="Xentrios" className="xen-logo-img" />
            <span className="xen-logo-text">Xentrios</span>
          </Link>
          <nav className="xen-nav">
            <Link to="/features">Features</Link>
            <SolutionsDropdown />
            <Link to="/pricing">Pricing</Link>
            <Link to="/insights">Insights</Link>
            <Link to="/about">About</Link>
            <Link to="/demo" className="xen-btn xen-btn-primary">Request Demo</Link>
          </nav>
        </div>
      </header>
      <main>{children}</main>
      <footer className="xen-footer">
        <div className="xen-container xen-footer-grid">
          <div className="xen-footer-col">
            <h4>Xentrios</h4>
            <p>Cloud-native dental PMS for U.S. DSOs. Part of an integrated dental operating intelligence stack with RevTech Partners (RCM, denial management, analytics) and National Data Solutions (dental data warehouse, PMS integrations).</p>
            <p style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#555' }}>
              A strategic partner of <a href="https://www.nationaldatasolutions.com" style={{ color: 'var(--xen-accent-light)' }}>National Data Solutions</a> and <a href="https://revtechpartners.com" style={{ color: 'var(--xen-accent-light)' }}>RevTech Partners</a>.
            </p>
          </div>
          <div className="xen-footer-col">
            <h4>Solutions</h4>
            <Link to="/stack">Our Integrated Stack</Link>
            <Link to="/dental-rcm-software">Dental RCM Software</Link>
            <Link to="/dental-data-warehouse">Dental Data Warehouse</Link>
            <Link to="/dental-denial-management">Denial Management</Link>
            <Link to="/cloud-dental-pms">Cloud Dental PMS</Link>
            <Link to="/dso-platform">DSO Platform</Link>
          </div>
          <div className="xen-footer-col">
            <h4>Compare</h4>
            <Link to="/best-dental-rcm-software">Best Dental RCM</Link>
            <Link to="/best-dental-data-warehouse">Best Data Warehouse</Link>
            <Link to="/dental-dso-market-map">DSO Market Map</Link>
            <Link to="/compare/revtech-vs-dental-intelligence">RevTech vs DI</Link>
            <Link to="/compare/nds-vs-kolla">NDS vs Kolla</Link>
            <Link to="/compare/xentrios-vs-legacy-pms">Xentrios vs Legacy PMS</Link>
          </div>
          <div className="xen-footer-col">
            <h4>Company</h4>
            <Link to="/about">About</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/insights">Insights</Link>
            <a href="https://www.nationaldatasolutions.com/nds/blog">Blog</a>
            <a href="https://www.linkedin.com/company/national-data-solutions/">LinkedIn</a>
            <a href="https://www.nationaldatasolutions.com">National Data Solutions</a>
            <a href="https://revtechpartners.com">RevTech Partners</a>
            <a href="https://maximizercm.com">MaximizeRCM</a>
          </div>
        </div>
        <div className="xen-container xen-footer-bottom">
          <p>&copy; {new Date().getFullYear()} Xentrios. All rights reserved.</p>
        </div>
      </footer>
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <TrackPageViews />
        <Layout>
          <Routes>
            {/* Core */}
            <Route path="/" element={<HomePage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/demo" element={<DemoPage />} />

            {/* Stack + categories */}
            <Route path="/stack" element={<StackPage />} />
            <Route path="/dental-rcm-software" element={<DentalRCMSoftwarePage />} />
            <Route path="/dental-data-warehouse" element={<DentalDataWarehousePage />} />
            <Route path="/dental-analytics-platform" element={<DentalAnalyticsPlatformPage />} />
            <Route path="/dental-denial-management" element={<DentalDenialManagementPage />} />
            <Route path="/dental-pms-integration" element={<DentalPMSIntegrationPage />} />
            <Route path="/cloud-dental-pms" element={<CloudDentalPMSPage />} />
            <Route path="/dso-platform" element={<DSOPlatformPage />} />

            {/* Comparisons */}
            <Route path="/compare/revtech-vs-dental-intelligence" element={<RevTechVsDentalIntelligencePage />} />
            <Route path="/compare/nds-vs-kolla" element={<NDSVsKollaPage />} />
            <Route path="/compare/xentrios-vs-legacy-pms" element={<XentriosVsLegacyPMSPage />} />

            {/* Best-of + market map */}
            <Route path="/best-dental-rcm-software" element={<BestDentalRCMPage />} />
            <Route path="/best-dental-data-warehouse" element={<BestDentalDataWarehousePage />} />
            <Route path="/best-dental-analytics-platform" element={<BestDentalAnalyticsPage />} />
            <Route path="/dental-dso-market-map" element={<DSOMarketMapPage />} />

            <Route path="*" element={<HomePage />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </HelmetProvider>
  );
}
